/**
 * Labstep
 *
 * @module containers/Entity/Read/Cursor
 * @desc Container for reading entities with cursor pagination
 */

import type { APICallOptions } from 'labstep-web/models';
import type { EntityName } from 'labstep-web/services/schema/schema';
import { readEntitiesCursor } from 'labstep-web/state/actions/entity';
import {
  selectCurrentCursor,
  selectCursorItems,
  selectCursorTotal,
  selectNextCursor,
  selectReadCursorStatus,
} from 'labstep-web/state/selectors/entity';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type {
  CursorOptions,
  IEntityReadCursorContainerChildrenProps,
  IEntityReadCursorContainerProps,
} from './types';

/**
 * Read Cursor Container connecting a component with state
 * Returns orginal component with
 * read function, status of action, nextCursor and entities
 */
export const EntityReadCursorContainer = <
  T extends EntityName = EntityName,
>({
  children,
  entityName,
  params,
  injectFields,
}: IEntityReadCursorContainerProps<T>): React.ReactElement | null => {
  const dispatch = useDispatch();

  // Use selectors to get state
  const entities = useSelector((state) =>
    selectCursorItems(state, entityName, params),
  );
  const status = useSelector((state) =>
    selectReadCursorStatus(state, entityName, params),
  );
  const currentCursor = useSelector((state) =>
    selectCurrentCursor(state, entityName, params),
  );
  const nextCursor = useSelector((state) =>
    selectNextCursor(state, entityName, params),
  );
  const total = useSelector((state) =>
    selectCursorTotal(state, entityName, params),
  );

  /**
   * Read entities cursor - memoized to prevent unnecessary re-renders
   * @param  {object} options - Additional options (onSucces, onFail e.t.c)
   * @param  {object} cursorOptions - Accepts 'mount' and 'refresh' bool fields
   * (overwrite all existing data with the newly fetched)
   */
  const read = useCallback(
    (options?: APICallOptions, cursorOptions: CursorOptions = {}) => {
      dispatch(
        readEntitiesCursor(
          entityName,
          params,
          options,
          cursorOptions,
          injectFields,
        ),
      );
    },
    [dispatch, entityName, params, injectFields],
  );

  return children({
    entities,
    status,
    currentCursor,
    nextCursor,
    total,
    read,
  });
};
